import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { KIND } from "@/components/ParticipantLinks";
import { RichTextEditor } from "@/components/RichTextEditor";
import { SmsButton } from "@/components/SendSmsDialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BookUser, Search, Loader2, Plus, Save, Users, Contact, Building2, GitMerge, AlertTriangle, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CRIMSON = "var(--brand)";

export default function Directory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") === "stakeholders" ? "stakeholders" : "contacts");
  const openName = searchParams.get("open");
  const consumeOpen = () => { const p = new URLSearchParams(searchParams); p.delete("open"); p.delete("tab"); setSearchParams(p, { replace: true }); };
  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="directory-page">
      <div className="flex items-center gap-2.5 mb-1">
        <BookUser className="h-5 w-5" style={{ color: CRIMSON }} />
        <h1 className="text-xl font-semibold font-brand text-slate-800">Directory</h1>
      </div>
      <p className="text-xs text-slate-500 mb-4">Browse and edit shared contacts & stakeholders, and see which participants each one is linked to.</p>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="directory-tabs">
          <TabsTrigger value="contacts" data-testid="dir-tab-contacts"><Contact className="h-3.5 w-3.5 mr-1.5" />Contacts</TabsTrigger>
          <TabsTrigger value="stakeholders" data-testid="dir-tab-stakeholders"><Building2 className="h-3.5 w-3.5 mr-1.5" />Stakeholders</TabsTrigger>
        </TabsList>
        <TabsContent value="contacts" className="mt-4"><DirectoryTab kindKey="contact" dirKey="contacts" openName={tab === "contacts" ? openName : null} onConsumed={consumeOpen} /></TabsContent>
        <TabsContent value="stakeholders" className="mt-4"><DirectoryTab kindKey="stakeholder" dirKey="stakeholders" openName={tab === "stakeholders" ? openName : null} onConsumed={consumeOpen} /></TabsContent>
      </Tabs>
    </div>
  );
}

function DirectoryTab({ kindKey, dirKey, openName, onConsumed }) {
  const cfg = KIND[kindKey];
  const { can } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null); // master name
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);

  const load = () => api.directory(dirKey).then(setRows).catch(() => { setRows([]); toast.error("Could not load directory."); });
  useEffect(() => { setRows(null); setSel(null); load(); /* eslint-disable-next-line */ }, [dirKey]);

  const selected = useMemo(() => (rows || []).find((r) => r.name === sel), [rows, sel]);

  const openRow = (m) => {
    setSel(m.name);
    setForm(Object.fromEntries(cfg.masterFields.map((f) => [f.k, m[f.k] ?? ""])));
    setCreating(false);
  };

  // Deep link: /directory?tab=…&open=<name> auto-opens that record (e.g. from a participant's linked contacts).
  useEffect(() => {
    if (!openName || !rows) return;
    const m = rows.find((r) => r.name === openName);
    if (m) { openRow(m); onConsumed && onConsumed(); }
    // eslint-disable-next-line
  }, [rows, openName]);
  const startCreate = () => {
    setSel("__new__"); setCreating(true);
    setForm(Object.fromEntries(cfg.masterFields.map((f) => [f.k, f.k === "status" ? "Active" : ""])));
  };

  const save = async () => {
    for (const f of cfg.masterFields) if (f.req && !String(form[f.k] || "").trim()) { toast.error(`${f.l} is required.`); return; }
    setSaving(true);
    try {
      if (creating) { await cfg.create(form); toast.success("Created."); }
      else { await cfg.update(sel, form); toast.success("Saved."); }
      await load();
      setSel(null); setForm(null); setCreating(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  const filtered = (rows || []).filter((m) => cfg.masterName(m).toLowerCase().includes(q.toLowerCase())
    || (m.organisation || "").toLowerCase().includes(q.toLowerCase()));

  if (rows === null) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: CRIMSON }} /></div>;

  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-4">
      {/* List */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-9 pl-9 text-sm" data-testid={`dir-${dirKey}-search`} />
          </div>
          {can.has("directory.edit") && <Button variant="outline" onClick={() => setMergeOpen(true)} className="h-9 shrink-0 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`dir-${dirKey}-merge`}><GitMerge className="h-4 w-4 mr-1" />Merge</Button>}
          {can.has("directory.edit") && <Button onClick={startCreate} style={{ backgroundColor: CRIMSON }} className="h-9 shrink-0" data-testid={`dir-${dirKey}-new`}><Plus className="h-4 w-4 mr-1" />New</Button>}
        </div>
        <div className="space-y-1.5 max-h-[70vh] overflow-y-auto pr-1" data-testid={`dir-${dirKey}-list`}>
          {filtered.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No records.</div>}
          {filtered.map((m) => (
            <button key={m.name} onClick={() => openRow(m)} data-testid={`dir-row-${m.name}`}
              className={`w-full text-left rounded-lg border p-2.5 transition-all ${sel === m.name ? "border-brand bg-brand/5" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-slate-800 truncate">{cfg.masterName(m)}</span>
                <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-[9px] py-0 shrink-0 flex items-center gap-1"><Users className="h-2.5 w-2.5" />{(m.linked || []).length}</Badge>
              </div>
              <div className="text-[10px] text-slate-400 truncate">{[m.name, m.organisation, m.profession, m.phone_number || m.phone, m.email].filter(Boolean).join(" · ")}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Editor */}
      <div>
        {!form ? (
          <div className="rounded-lg border border-dashed border-slate-200 h-full min-h-[200px] flex items-center justify-center text-sm text-slate-400">
            Select a record to view & edit, or create a new one.
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`dir-${dirKey}-editor`}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{creating ? `New ${kindKey}` : `${cfg.title.slice(0, -1)} · ${sel}`}</div>
              {!creating && can.has("directory.edit") && (
                <SmsButton type={kindKey} id={sel} name={cfg.masterName(selected || {})}
                  mobile={form.phone_number || form.phone || ""} />
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {cfg.masterFields.map((f) => (
                <div key={f.k} className={f.area ? "sm:col-span-2" : ""}>
                  <Label className="text-[11px] font-semibold text-slate-600">{f.l}{f.req ? <span className="text-rose-500"> *</span> : null}</Label>
                  {f.opts ? (
                    <Select value={form[f.k] || ""} onValueChange={(v) => setForm({ ...form, [f.k]: v })}>
                      <SelectTrigger className="mt-1 h-9 text-sm" data-testid={`dir-f-${f.k}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent>{f.opts.map((o) => <SelectItem key={o} value={o} className="text-sm">{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : f.rich ? (
                    <RichTextEditor value={form[f.k] || ""} disabled={!can.has("directory.edit")} onChange={(v) => setForm({ ...form, [f.k]: v })} testid={`dir-f-${f.k}`} />
                  ) : f.area ? (
                    <Textarea value={form[f.k] || ""} disabled={!can.has("directory.edit")} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className="mt-1 text-sm min-h-[54px]" data-testid={`dir-f-${f.k}`} />
                  ) : (
                    <Input value={form[f.k] || ""} disabled={!can.has("directory.edit")} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className="mt-1 h-9 text-sm" data-testid={`dir-f-${f.k}`} />
                  )}
                </div>
              ))}
            </div>

            {!creating && (
              <div className="mt-3 rounded-md border border-slate-100 bg-slate-50/60 p-2.5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5"><Users className="h-3 w-3" /> Linked participants ({(selected?.linked || []).length})</div>
                <div className="flex flex-wrap gap-1.5" data-testid="dir-linked-participants">
                  {(selected?.linked || []).length === 0 && <span className="text-[11px] text-slate-400">Not linked to any participant yet.</span>}
                  {(selected?.linked || []).map((p) => (
                    <button
                      key={p.name}
                      type="button"
                      onClick={() => navigate(`/participants?open=${encodeURIComponent(p.name)}`)}
                      data-testid={`dir-linked-${p.name}`}
                      title={`View ${p.display_name}`}
                      className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] text-slate-600 transition-colors hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700"
                    >
                      {p.display_name}
                      <ArrowRight className="h-2.5 w-2.5" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => { setSel(null); setForm(null); setCreating(false); }}>Cancel</Button>
              {can.has("directory.edit") && <Button onClick={save} disabled={saving} style={{ backgroundColor: CRIMSON }} data-testid={`dir-${dirKey}-save`}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}{creating ? "Create" : "Save"}
              </Button>}
            </div>
          </div>
        )}
      </div>

      {mergeOpen && <MergeDialog cfg={cfg} dirKey={dirKey} rows={rows}
        onClose={() => setMergeOpen(false)}
        onDone={async () => { setMergeOpen(false); setSel(null); setForm(null); await load(); }} />}
    </div>
  );
}

function MergeDialog({ cfg, dirKey, rows, onClose, onDone }) {
  const [keep, setKeep] = useState("");
  const [remove, setRemove] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  const rec = (n) => rows.find((r) => r.name === n);
  const keepRec = rec(keep);
  const removeRec = rec(remove);
  const removeCount = (removeRec?.linked || []).length;

  const doMerge = async () => {
    if (!keep || !remove) { toast.error("Choose both records."); return; }
    if (keep === remove) { toast.error("Pick two different records."); return; }
    setBusy(true);
    try {
      const r = await api.mergeDirectory(dirKey, keep, remove);
      toast.success(`Merged. ${r.relinked_participants} participant(s) re-linked; duplicate deleted.`);
      await onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Merge failed."); }
    finally { setBusy(false); }
  };

  const picker = (value, setValue, exclude, label, testid) => (
    <div>
      <Label className="text-[11px] font-semibold text-slate-600">{label}</Label>
      <Select value={value} onValueChange={setValue}>
        <SelectTrigger className="mt-1 h-9 text-sm" data-testid={testid}><SelectValue placeholder="Select record…" /></SelectTrigger>
        <SelectContent className="max-h-72">
          {rows.filter((r) => r.name !== exclude).map((r) => (
            <SelectItem key={r.name} value={r.name} className="text-sm">
              {cfg.masterName(r)} · {r.name} ({(r.linked || []).length} linked)
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid={`${dirKey}-merge-dialog`}>
        <DialogHeader>
          <DialogTitle className="text-base font-brand flex items-center gap-2"><GitMerge className="h-4 w-4" style={{ color: CRIMSON }} />Merge duplicates</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500">Combine two duplicated records into one. Every participant linked to the duplicate will be re-linked to the record you keep, then the duplicate is deleted.</p>

        <div className="space-y-3 mt-1">
          {picker(keep, (v) => { setKeep(v); if (v === remove) setRemove(""); }, remove, "Record to KEEP", "merge-keep")}
          {picker(remove, (v) => { setRemove(v); if (v === keep) setKeep(""); }, keep, "Record to REMOVE (deleted)", "merge-remove")}

          {keepRec && removeRec && (
            <div className="flex items-center justify-center gap-2 text-xs text-slate-600 rounded-md bg-slate-50 border border-slate-200 p-2.5" data-testid="merge-summary">
              <span className="font-medium text-rose-600 line-through">{cfg.masterName(removeRec)}</span>
              <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
              <span className="font-medium text-emerald-700">{cfg.masterName(keepRec)}</span>
            </div>
          )}

          <div className="flex items-start gap-2 rounded-md bg-rose-50 border border-rose-200 p-3">
            <AlertTriangle className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />
            <div className="text-[11px] text-rose-700">
              <b>This cannot be undone.</b> The duplicate record will be permanently deleted{removeRec ? ` and ${removeCount} participant link(s) will be moved to the kept record` : ""}.
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="accent-brand" data-testid="merge-ack" />
            I understand this permanently deletes the duplicate and cannot be undone.
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={doMerge} disabled={busy || !ack || !keep || !remove} className="bg-rose-600 hover:bg-rose-700" data-testid="merge-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <GitMerge className="h-4 w-4 mr-1.5" />}Merge & delete duplicate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
